import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiFehler, api, fehlertext, strom, stromAnhaengen } from '../api'
import { notierungenApi } from '../api_notierungen'
import { bereichVon } from '../bereiche'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import UiBlock, { uiBlockLesen } from '../komponenten/UiBlock'
import WerteTabelle, { ergebnisLesen } from '../komponenten/WerteTabelle'
import { datum, stueck, zeitpunkt } from '../format'
import { WERKZEUGZUSTAND } from '../texte'
import type { BereichId, ChatEreignis, NachrichtAnzeige, Unterhaltung, UnterhaltungVoll, WerkzeugAnzeige } from '../typen'

/**
 * Der Assistent: ein Gespräch, in dem alles geht, was die Oberfläche kann.
 *
 * Der Text des Assistenten erscheint, während er entsteht. Jede Aktion, die er ausführt, steht
 * als Karte im Verlauf - Werkzeug, Argumente, Ergebnis -, so dass nachvollziehbar bleibt, was
 * aus welchem Satz wurde. Aktionen, die buchen, entscheiden oder löschen, hält das Backend an:
 * die Karte bekommt zwei Knöpfe, und erst „Ausführen" tut etwas. Das Gespräch bleibt gespeichert.
 *
 * Jeder Bereich hat seinen eigenen Assistenten (06.10.2026): eigene Gespräche, eigene Werkzeuge im
 * Backend, eigene Adresse (`/assistent`, `/notierungen/assistent`). Die Seite bekommt den Bereich als
 * Eigenschaft; App.tsx setzt `key={bereich}`, damit beim Wechsel ein frischer Zustand beginnt.
 */

type Eintrag = NachrichtAnzeige

/**
 * Der Abbruchvermerk der Plattform (02.11.2026): bricht ein Lauf ab (Modell, Fehler, Neustart), speichert das
 * Backend eine Assistentennachricht „Lauf abgebrochen um …“. Sie ist kein Gesprächsbeitrag, sondern eine Spur -
 * darum leise, aber sichtbar, damit nach dem Neuladen niemand rätselt, ob der letzte Auftrag lief.
 */
const ABBRUCH_ANFANG = 'Lauf abgebrochen um '
function istAbbruch(e: Eintrag): boolean {
  return e.rolle === 'assistant' && e.werkzeuge.length === 0 && e.text.startsWith(ABBRUCH_ANFANG)
}


// --- Markdown, so viel wie der Assistent braucht: Absätze, Listen, Tabellen, fett, Code, Links -----

function inline(text: string, schluessel: string): ReactNode[] {
  const teile = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g)
  return teile.map((teil, i) => {
    const k = `${schluessel}-${i}`
    if (teil.startsWith('**') && teil.endsWith('**')) return <b key={k}>{teil.slice(2, -2)}</b>
    if (teil.startsWith('`') && teil.endsWith('`')) return <code key={k}>{teil.slice(1, -1)}</code>
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(teil)
    if (link) {
      const [, beschriftung, ziel] = link
      if (ziel.startsWith('/api/export')) {
        return (
          <a key={k} href={ziel} download>
            {beschriftung}
          </a>
        )
      }
      if (ziel.startsWith('/')) {
        return (
          <Link key={k} to={ziel}>
            {beschriftung}
          </Link>
        )
      }
      return (
        <a key={k} href={ziel} target="_blank" rel="noreferrer">
          {beschriftung}
        </a>
      )
    }
    return <Fragment key={k}>{teil}</Fragment>
  })
}

function zeilenMitUmbruch(zeilen: string[], schluessel: string): ReactNode[] {
  return zeilen.flatMap((zeile, i) => [
    ...inline(zeile.replace(/^#+\s*/, ''), `${schluessel}-${i}`),
    ...(i < zeilen.length - 1 ? [<br key={`${schluessel}-br-${i}`} />] : []),
  ])
}

function tabelle(zeilen: string[], schluessel: string): ReactNode {
  const zellen = zeilen
    .filter((z) => !/^\|?\s*:?-{2,}/.test(z.replace(/\|/g, '').trim()) || !/^[\s|:-]+$/.test(z))
    .map((z) =>
      z
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((x) => x.trim()),
    )
  if (zellen.length === 0) return null
  const [kopf, ...koerper] = zellen
  return (
    <div className="tabellenrahmen chat-tabelle" key={schluessel}>
      <table>
        <thead>
          <tr>
            {kopf.map((z, i) => (
              <th key={i}>{inline(z, `${schluessel}-h${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {koerper.map((zeile, r) => (
            <tr key={r}>
              {zeile.map((z, i) => (
                <td key={i} className={/^[\d.,\s%€t-]+$/.test(z) && z !== '' ? 'zahl' : ''}>
                  {inline(z, `${schluessel}-${r}-${i}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Was ein Markdown-Block an Oberfläche braucht: den Weg zurück in den Chat und den Sperrzustand. */
export interface MarkdownUmfeld {
  senden?: (text: string) => void
  gesperrt?: boolean
  spaetereAntworten?: string[]
}

type Abschnitt = { art: 'text'; text: string } | { art: 'zaun'; sprache: string; inhalt: string; geschlossen: boolean }

/** Den Text in Zaunblöcke (```) und Markdown dazwischen teilen. Ein offener Zaun läuft bis zum Ende. */
function abschnitte(text: string): Abschnitt[] {
  const ergebnis: Abschnitt[] = []
  const zeilen = text.split('\n')
  let offen: { sprache: string; zeilen: string[] } | null = null
  let puffer: string[] = []
  for (const zeile of zeilen) {
    const zaun = /^\s*```\s*(\S*)\s*$/.exec(zeile)
    if (offen) {
      if (zaun && zaun[1] === '') {
        ergebnis.push({ art: 'zaun', sprache: offen.sprache, inhalt: offen.zeilen.join('\n'), geschlossen: true })
        offen = null
      } else {
        offen.zeilen.push(zeile)
      }
    } else if (zaun) {
      if (puffer.length > 0) ergebnis.push({ art: 'text', text: puffer.join('\n') })
      puffer = []
      offen = { sprache: zaun[1].toLowerCase(), zeilen: [] }
    } else {
      puffer.push(zeile)
    }
  }
  if (offen) ergebnis.push({ art: 'zaun', sprache: offen.sprache, inhalt: offen.zeilen.join('\n'), geschlossen: false })
  else if (puffer.length > 0) ergebnis.push({ art: 'text', text: puffer.join('\n') })
  return ergebnis
}

/** Ein Zaunblock: ```ui wird zur Oberfläche, alles andere (und jeder kaputte ui-Block) bleibt Code. */
function zaun(a: Extract<Abschnitt, { art: 'zaun' }>, k: string, umfeld: MarkdownUmfeld): ReactNode {
  if (a.sprache === 'ui') {
    if (!a.geschlossen) {
      return (
        <p key={k} className="hinweis ui-aufbau">
          Oberfläche wird aufgebaut.
        </p>
      )
    }
    const daten = uiBlockLesen(a.inhalt)
    if (daten) {
      return (
        <UiBlock key={k} daten={daten} senden={umfeld.senden} gesperrt={umfeld.gesperrt} spaetereAntworten={umfeld.spaetereAntworten} />
      )
    }
  }
  return (
    <pre key={k} className="chat-code">
      {a.inhalt}
    </pre>
  )
}

export function markdown(text: string, umfeld: MarkdownUmfeld = {}): ReactNode {
  return abschnitte(text.replace(/\r/g, '')).flatMap((a, n) => {
    if (a.art === 'zaun') return [zaun(a, `z${n}`, umfeld)]
    return a.text.split(/\n\s*\n/).map((block, b) => markdownBlock(block, `b${n}-${b}`))
  })
}

function markdownBlock(block: string, k: string): ReactNode {
  {
    const zeilen = block.split('\n').filter((z) => z.trim() !== '')
    if (zeilen.length === 0) return null
    if (zeilen.every((z) => z.trim().startsWith('|'))) return tabelle(zeilen, k)
    if (zeilen.every((z) => /^\s*[-*]\s+/.test(z))) {
      return (
        <ul key={k}>
          {zeilen.map((z, i) => (
            <li key={i}>{inline(z.replace(/^\s*[-*]\s+/, ''), `${k}-${i}`)}</li>
          ))}
        </ul>
      )
    }
    if (zeilen.every((z) => /^\s*\d+[.)]\s+/.test(z))) {
      return (
        <ol key={k}>
          {zeilen.map((z, i) => (
            <li key={i}>{inline(z.replace(/^\s*\d+[.)]\s+/, ''), `${k}-${i}`)}</li>
          ))}
        </ol>
      )
    }
    if (zeilen.length === 1 && /^#{1,3}\s/.test(zeilen[0])) {
      return <h3 key={k}>{inline(zeilen[0].replace(/^#+\s*/, ''), k)}</h3>
    }
    return <p key={k}>{zeilenMitUmbruch(zeilen, k)}</p>
  }
}

// --- Werkzeugkarte ------------------------------------------------------------------------------

// Die Argumente, die in der Kurzform zuerst genannt werden - das, was Frau Barth wiedererkennt:
// Blatt, Artikel, Tag. Der Rest folgt in der Reihenfolge des Aufrufs; das ganze JSON steht im Tooltip.
const ARGUMENTE_VORN = [
  'gruppe',
  'artikelnummer',
  'artikel',
  'material',
  'fertigartikel',
  'lieferant',
  'id',
  'lauf_id',
  'import_lauf_id',
  'stichtag',
  'tag',
  'von',
  'bis',
  'jahr',
  'monat',
  'status',
  'art',
  'menge_t',
  'text',
]

/** Ein einzelner Wert in Worten: ISO-Tage als TT.MM.JJJJ, Wahrheitswerte als ja/nein, Listen kurz. */
function argumentText(wert: unknown): string | null {
  if (wert === null || wert === undefined || wert === '') return null
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  // Jahre und Nummern (Lauf-, Bestell-, Artikel-Ids) ohne Tausenderpunkt - „2.026" liest niemand als Jahr.
  if (typeof wert === 'number') return Number.isInteger(wert) && Math.abs(wert) < 100000 ? String(wert) : wert.toLocaleString('de-DE')
  if (typeof wert === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(wert)) return datum(wert)
    return wert.length > 40 ? `${wert.slice(0, 40)}…` : wert
  }
  if (Array.isArray(wert)) {
    const teile = wert.map(argumentText).filter((t): t is string => t !== null)
    if (teile.length === 0) return null
    return teile.length > 3 ? `${teile.slice(0, 3).join(', ')} … (${teile.length})` : teile.join(', ')
  }
  return null
}

/** Die wichtigsten Argumente in Worten: „OTH.-Würfel, 03.09.2026" statt `{"gruppe":"OTH.-Würfel",…}`. */
function argumenteKurz(argumente: Record<string, unknown>): string {
  const schluessel = Object.keys(argumente)
  const geordnet = [
    ...ARGUMENTE_VORN.filter((k) => schluessel.includes(k)),
    ...schluessel.filter((k) => !ARGUMENTE_VORN.includes(k)),
  ]
  const teile: string[] = []
  for (const k of geordnet) {
    const text = argumentText(argumente[k])
    if (text !== null) teile.push(text)
    if (teile.length === 4) break
  }
  return teile.join(', ')
}

/** Die Abfrage von `rechnen_abfrage` - sie ist das, was die Karte erklärt, und gehört nicht ins JSON. */
function sqlVon(werkzeug: WerkzeugAnzeige): string | null {
  if (werkzeug.name !== 'rechnen_abfrage') return null
  const sql = werkzeug.argumente.sql
  return typeof sql === 'string' && sql.trim() !== '' ? sql : null
}

/** Der Anfang einer Abfrage in einer Zeile: Umbrüche und doppelte Leerzeichen weg, nach 70 Zeichen Auslassung. */
function sqlKurz(sql: string): string {
  const eine = sql.replace(/\s+/g, ' ').trim()
  return eine.length > 70 ? `${eine.slice(0, 70)}…` : eine
}

/** Was eine Aktion bewirkt, in einer Zeile: die Beschreibung des Backends, sonst Titel und Argumente in Worten. */
function aktionKurz(werkzeug: WerkzeugAnzeige): string {
  return werkzeug.beschreibung?.trim() || `${werkzeug.titel}: ${argumenteKurz(werkzeug.argumente)}`
}

/**
 * Eine Aktion im Verlauf. Seit dem 02.11.2026 steht vorn, was sie bewirkt („Lieferant Beucke: Puffer
 * 42 → 56 Tage"), und hervorgehoben, für wen sie gilt („gilt für alle 12 Artikel von Beucke") - die
 * Freigabekarte zeigte vorher nur „lieferant_id 5", und Frau Barth gab frei, ohne zu sehen, was. Die
 * rohen Argumente bleiben eingeklappt darunter. Ohne Beschreibung (älteres Backend) wie bisher.
 */
function WerkzeugKarte({
  werkzeug,
  entscheiden,
  gesperrt,
}: {
  werkzeug: WerkzeugAnzeige
  entscheiden: (id: string, erlaubt: boolean) => void
  gesperrt: boolean
}) {
  const wartet = werkzeug.zustand === 'wartet'
  const beschreibung = werkzeug.beschreibung?.trim() || null
  const umfang = werkzeug.umfang?.trim() || null
  const sql = sqlVon(werkzeug)
  // Ohne die Abfrage bleiben bei rechnen_abfrage meist keine Argumente - dann kein leeres JSON.
  const argumente = sql === null ? werkzeug.argumente : Object.fromEntries(Object.entries(werkzeug.argumente).filter(([k]) => k !== 'sql'))
  if (werkzeug.name === 'werkzeuge_laden') {
    const bereich = String(werkzeug.argumente.bereich ?? '')
    return (
      <div className="hinweis werkzeug-klein">
        {werkzeug.zustand === 'laeuft' ? `Werkzeuge für „${bereich}“ werden geladen.` : `Werkzeuge für „${bereich}“ geladen.`}
      </div>
    )
  }
  return (
    <details className={`werkzeug ${werkzeug.zustand}`} open={wartet}>
      <summary>
        <span className={`status ${WERKZEUGZUSTAND[werkzeug.zustand].klasse}`}>{WERKZEUGZUSTAND[werkzeug.zustand].text}</span>
        <span className="werkzeug-titel">{werkzeug.titel}</span>
        <span className={`werkzeug-kurz${beschreibung ? '' : ' hinweis'}`} title={sql ?? JSON.stringify(werkzeug.argumente, null, 2)}>
          {beschreibung ?? (sql === null ? argumenteKurz(werkzeug.argumente) : sqlKurz(sql))}
        </span>
      </summary>
      <div className="werkzeug-inhalt">
        {beschreibung && <p className="werkzeug-beschreibung">{beschreibung}</p>}
        {umfang && <p className="freigabe-umfang">{umfang}</p>}
        {sql !== null && (
          <pre className="werkzeug-sql">
            <code>{sql}</code>
          </pre>
        )}
        {Object.keys(argumente).length > 0 &&
          (beschreibung ? (
            <details className="werkzeug-roh">
              <summary>Einzelheiten</summary>
              <WerteTabelle wert={argumente} />
            </details>
          ) : (
            <WerteTabelle wert={argumente} />
          ))}
        {werkzeug.text && werkzeug.zustand !== 'wartet' && (
          <div className="werkzeug-ergebnis-rahmen">
            <WerteTabelle wert={ergebnisLesen(werkzeug.text)} />
          </div>
        )}
        {wartet && (
          <div className="leiste" style={{ marginTop: 8, marginBottom: 0 }}>
            <span className={werkzeug.grund ? 'freigabe-grund' : undefined}>
              {werkzeug.grund ? `Diese Aktion braucht eine Freigabe: ${werkzeug.grund}. Ausführen?` : 'Diese Aktion bucht, entscheidet oder löscht. Ausführen?'}
            </span>
            <button className="haupt" disabled={gesperrt} onClick={() => entscheiden(werkzeug.id, true)}>
              Ausführen
            </button>
            <button disabled={gesperrt} onClick={() => entscheiden(werkzeug.id, false)}>
              Ablehnen
            </button>
          </div>
        )}
      </div>
    </details>
  )
}

/**
 * Mehrere wartende Aktionen auf einmal entscheiden. „MVN für alle Artikel" ergab 65 Karten mit je
 * einem Knopf - sicher, aber unbedienbar (Befund 19.09.2026). Seit dem 02.11.2026 schon ab zwei
 * (ein Gespräch hatte acht Einzelfreigaben nacheinander) und mit der Liste dessen, was freigegeben
 * wird - in Worten, nicht als Ids.
 */
function Sammelfreigabe({
  wartend,
  gesperrt,
  entscheiden,
}: {
  wartend: WerkzeugAnzeige[]
  gesperrt: boolean
  entscheiden: (kennungen: string[], erlaubt: boolean) => void
}) {
  if (wartend.length < 2) return null
  const kennungen = wartend.map((w) => w.id)
  return (
    <div className="leiste sammelfreigabe">
      <span>{wartend.length} Aktionen warten auf eine Freigabe:</span>
      <ul className="sammelfreigabe-liste">
        {wartend.slice(0, SAMMEL_LISTE_MAX).map((w) => (
          <li key={w.id}>
            {aktionKurz(w)}
            {w.umfang?.trim() && <span className="freigabe-umfang-kurz"> - {w.umfang}</span>}
          </li>
        ))}
        {wartend.length > SAMMEL_LISTE_MAX && <li className="hinweis">und {wartend.length - SAMMEL_LISTE_MAX} weitere</li>}
      </ul>
      <button className="haupt" disabled={gesperrt} onClick={() => entscheiden(kennungen, true)}>
        Alle ausführen
      </button>
      <button disabled={gesperrt} onClick={() => entscheiden(kennungen, false)}>
        Alle ablehnen
      </button>
      <span className="hinweis">Einzeln geht weiter unten, Karte für Karte.</span>
    </div>
  )
}

// --- Verbindungsabbruch ---------------------------------------------------------------------------

/**
 * Ist die Verbindung mitten im Strom gerissen? Der Browser wirft dann einen TypeError ohne Status
 * („Failed to fetch", „network error", ERR_NETWORK_CHANGED) - kein ApiFehler mit einer Antwort des
 * Servers. Der Server rechnet weiter und speichert die Antwort; nur der Weg zu uns fehlt.
 */
function istVerbindungsabbruch(f: unknown): boolean {
  if (f instanceof ApiFehler) return false
  if (!(f instanceof Error)) return false
  return f instanceof TypeError || /network|fetch|ERR_/i.test(f.message)
}

/**
 * Ist die letzte Antwort auf dem Server vollständig? Ja, wenn eine Freigabe wartet - oder wenn kein
 * Werkzeug mehr läuft und ein Text da ist. Endet der Verlauf mit der Nutzernachricht, hat der Server
 * noch nichts gespeichert und arbeitet noch.
 */
function antwortVollstaendig(voll: UnterhaltungVoll): boolean {
  if (voll.unterhaltung.wartet) return true
  const letzte = voll.nachrichten[voll.nachrichten.length - 1]
  if (!letzte || letzte.rolle !== 'assistant') return false
  if (letzte.werkzeuge.some((w) => w.zustand === 'wartet')) return true
  return !letzte.werkzeuge.some((w) => w.zustand === 'laeuft') && letzte.text.trim() !== ''
}

const NACHLADEN_ABSTAND_MS = 3000
const NACHLADEN_HOECHSTENS_MS = 5 * 60 * 1000
/** So lange hängt sich die Seite an einen Lauf auf dem Server - ein großer Auftrag braucht Minuten. */
const MITLAUFEN_HOECHSTENS_MS = 15 * 60 * 1000
/** So viele wartende Aktionen nennt die Sammelfreigabe einzeln; der Rest wird gezählt. */
const SAMMEL_LISTE_MAX = 12

function pause(ms: number): Promise<void> {
  return new Promise((fertig) => setTimeout(fertig, ms))
}

// --- Die Seite ----------------------------------------------------------------------------------

/** Welche Dateien der Assistent eines Bereichs annimmt - und wie sie hochgeladen werden. */
const ANHANG: Record<BereichId, { beschriftung: string; annehmen: string }> = {
  verpackung: {
    beschriftung: 'Datei anhängen (PDF oder Excel)',
    annehmen:
      '.pdf,.xlsx,.xlsm,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12',
  },
  notierungen: {
    beschriftung: 'Notierung anhängen (PDF, Bild oder Mail)',
    annehmen: '.pdf,.png,.jpg,.jpeg,.eml,.txt,application/pdf,image/png,image/jpeg,message/rfc822,text/plain',
  },
}

export default function AssistentSeite({ bereich }: { bereich: BereichId }) {
  const vorgaben = bereichVon(bereich).assistent
  const basis = `${bereichVon(bereich).praefix}/assistent`
  const [suchparameter, setSuchparameter] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [unterhaltungen, setUnterhaltungen] = useState<Unterhaltung[]>([])
  const [aktuell, setAktuell] = useState<number | null>(null)
  const [eintraege, setEintraege] = useState<Eintrag[]>([])
  const [eingabe, setEingabe] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  // Der Lauf arbeitet auf dem Server, die Seite hängt nur mit (nach Neuladen oder Verbindungsabbruch).
  // Dann darf man in ein anderes Gespräch wechseln - der Server rechnet ohnehin weiter.
  const [aufServer, setAufServer] = useState(false)
  // Solange das Backend eine Excel-Datei liest (mit KI, bei großen Dateien Minuten), ist das
  // Gespräch gesperrt wie beim Laufen des Assistenten - sonst kommt die Meldung ins Leere.
  const [liestExcel, setLiestExcel] = useState(false)
  const [wartet, setWartet] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  // Was nicht schiefging, aber gesagt werden muss: „Die Verbindung war kurz unterbrochen …".
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [tokens, setTokens] = useState({ eingabe: 0, ausgabe: 0 })
  const gewuenscht = useRef<number | null>(null)   // das Gespräch, das der Nutzer zuletzt gewählt hat
  const neueRunde = useRef(true)
  const letzterTyp = useRef<string>('')      // die Art des letzten Stromereignisses: Text nach einem Werkzeug wird ein Absatz
  const ende = useRef<HTMLDivElement>(null)
  const gestartet = useRef(false)
  // Wer `laeuft` beenden darf: nur der jüngste Lauf. Ein alter Strom, dessen Gespräch man verlassen hat,
  // soll dem neuen nicht „fertig" melden.
  const laufNummer = useRef(0)
  const anhaengenAbbruch = useRef<AbortController | null>(null)

  const listeLaden = useCallback(async () => {
    const werte = await api.unterhaltungen(bereich)
    setUnterhaltungen(werte)
    return werte
  }, [bereich])

  /** Ein Ereignis des Stroms in den Verlauf einarbeiten. */
  const anwenden = useCallback((e: ChatEreignis) => {
    if (e.typ === 'text') {
      // Text, der nach einer Werkzeugkarte weitergeht, beginnt einen neuen Absatz - sonst klebt
      // „…nach der Bestätigung.Die Bestellung wurde gelöscht." aneinander (Sandbox-Befund 17.09.2026).
      const absatz = letzterTyp.current !== 'text'
      letzterTyp.current = 'text'
      setEintraege((alt) => {
        const letzter = alt[alt.length - 1]
        if (letzter && letzter.rolle === 'assistant' && !neueRunde.current) {
          const trenner = absatz && letzter.text && !letzter.text.endsWith('\n') ? '\n\n' : ''
          return [...alt.slice(0, -1), { ...letzter, text: letzter.text + trenner + e.text }]
        }
        neueRunde.current = false
        return [...alt, { id: -Date.now(), rolle: 'assistant', text: e.text, werkzeuge: [], angelegt_am: new Date().toISOString() }]
      })
    } else if (e.typ === 'aufruf' || e.typ === 'freigabe') {
      letzterTyp.current = e.typ
      const werkzeug: WerkzeugAnzeige = {
        id: e.id,
        name: e.name,
        titel: e.titel,
        argumente: e.argumente,
        zustand: e.typ === 'freigabe' ? 'wartet' : 'laeuft',
        text: '',
        grund: e.typ === 'freigabe' ? e.grund : undefined,
        beschreibung: e.typ === 'freigabe' ? e.beschreibung : undefined,
        umfang: e.typ === 'freigabe' ? e.umfang : undefined,
      }
      setEintraege((alt) => {
        // Nach einer Freigabe meldet der Server den Aufruf noch einmal - dann die Karte erneuern, nicht doppeln.
        if (alt.some((eintrag) => eintrag.werkzeuge.some((w) => w.id === werkzeug.id))) {
          return alt.map((eintrag) => ({
            ...eintrag,
            werkzeuge: eintrag.werkzeuge.map((w) => (w.id === werkzeug.id ? { ...w, zustand: werkzeug.zustand } : w)),
          }))
        }
        const letzter = alt[alt.length - 1]
        if (letzter && letzter.rolle === 'assistant' && !neueRunde.current) {
          return [...alt.slice(0, -1), { ...letzter, werkzeuge: [...letzter.werkzeuge, werkzeug] }]
        }
        neueRunde.current = false
        return [...alt, { id: -Date.now(), rolle: 'assistant', text: '', werkzeuge: [werkzeug], angelegt_am: new Date().toISOString() }]
      })
    } else if (e.typ === 'ergebnis') {
      letzterTyp.current = 'ergebnis'
      neueRunde.current = true
      const zustand: WerkzeugAnzeige['zustand'] = e.abgelehnt ? 'abgelehnt' : e.status === 0 || e.status >= 400 ? 'fehler' : 'fertig'
      setEintraege((alt) =>
        alt.map((eintrag) =>
          eintrag.werkzeuge.some((w) => w.id === e.id)
            ? { ...eintrag, werkzeuge: eintrag.werkzeuge.map((w) => (w.id === e.id ? { ...w, zustand, text: e.text } : w)) }
            : eintrag,
        ),
      )
    } else if (e.typ === 'wartet') {
      setWartet(true)
    } else if (e.typ === 'fehler') {
      setFehler(e.text)
    } else if (e.typ === 'verbrauch') {
      setTokens((alt) => ({ eingabe: alt.eingabe + (e.prompt_tokens ?? 0), ausgabe: alt.ausgabe + (e.completion_tokens ?? 0) }))
    }
  }, [])

  /** Den Verlauf einer Unterhaltung vom Server nehmen - beim Öffnen und nach einem Verbindungsabbruch. */
  const verlaufLaden = useCallback(async (id: number) => {
    const voll = await api.unterhaltung(id)
    // Hat der Nutzer inzwischen ein anderes Gespräch gewählt (Klick auf „Neues Gespräch" während die Liste
    // noch lud), gehört dieser Verlauf nicht mehr auf den Schirm - sonst landen seine nächsten Nachrichten
    // im alten Gespräch, während der Auftakt im neuen läuft (Sandbox-Befund 17.09.2026).
    if (gewuenscht.current !== null && gewuenscht.current !== id) return null
    setEintraege(voll.nachrichten)
    setWartet(voll.unterhaltung.wartet)
    neueRunde.current = true
    return voll
  }, [])

  const laufBeginnen = useCallback(() => {
    setLaeuft(true)
    return ++laufNummer.current
  }, [])

  const laufBeenden = useCallback((nummer: number) => {
    if (nummer !== laufNummer.current) return
    setLaeuft(false)
    setAufServer(false)
    neueRunde.current = true
  }, [])

  /** Beim Wechsel des Gesprächs: das Mitlaufen am alten lösen - dort rechnet der Server ohne uns weiter. */
  const mitlaufenLoesen = useCallback(() => {
    anhaengenAbbruch.current?.abort()
    anhaengenAbbruch.current = null
    laufNummer.current++
    setLaeuft(false)
    setAufServer(false)
  }, [])

  /**
   * An einen Lauf hängen, der auf dem Server weiterarbeitet (02.11.2026): über `/strom` kommen die
   * Ereignisse ab jetzt; endet der Strom, wird der Verlauf neu geladen, und läuft der Server dann noch,
   * geht es nach einer Pause wieder an den Strom. Reißt die Verbindung (oder kennt das Backend `/strom`
   * nicht), bleibt nur das Neuladen alle drei Sekunden. Vorher stand nach einem Abbruch „abgebrochen",
   * obwohl der Server weiterbuchte - und am nächsten Morgen die Frage „Ist das jetzt schon erledigt?".
   */
  const mitlaufen = useCallback(
    async (id: number) => {
      const beginn = Date.now()
      setAufServer(true)
      for (;;) {
        if (gewuenscht.current !== id) return
        const abbruch = new AbortController()
        anhaengenAbbruch.current = abbruch
        neueRunde.current = true
        try {
          await stromAnhaengen(id, (e) => { if (gewuenscht.current === id) anwenden(e) }, abbruch.signal)
        } catch (f) {
          if (abbruch.signal.aborted) return
          if (!istVerbindungsabbruch(f) && !(f instanceof ApiFehler && f.status === 404)) {
            setFehler(fehlertext(f))
            return
          }
        } finally {
          if (anhaengenAbbruch.current === abbruch) anhaengenAbbruch.current = null
        }
        let voll: UnterhaltungVoll | null = null
        try {
          voll = await verlaufLaden(id)
        } catch {
          // Noch keine Verbindung - nach der Pause noch einmal.
        }
        if (gewuenscht.current !== id) return
        if (voll && !voll.unterhaltung.laeuft) return
        if (Date.now() - beginn > MITLAUFEN_HOECHSTENS_MS) {
          setFehler('Der Assistent arbeitet nach einer Viertelstunde noch auf dem Server. Die Antwort wird dort gespeichert - bitte das Gespräch später in der Liste erneut öffnen.')
          return
        }
        await pause(NACHLADEN_ABSTAND_MS)
      }
    },
    [anwenden, verlaufLaden],
  )

  /** Mitlaufen als eigener Lauf: nach dem Öffnen eines Gesprächs, das auf dem Server noch arbeitet. */
  const begleiten = useCallback(
    async (id: number) => {
      const nummer = laufBeginnen()
      try {
        await mitlaufen(id)
      } finally {
        laufBeenden(nummer)
        void listeLaden()
      }
    },
    [laufBeenden, laufBeginnen, listeLaden, mitlaufen],
  )

  /**
   * Nach einem Verbindungsabbruch: den Verlauf vom Server holen, statt einen halben Text und
   * „network error" stehen zu lassen. Sagt der Server `laeuft`, hängt sich die Seite wieder an; ein
   * älteres Backend ohne `laeuft` wird alle drei Sekunden neu gelesen, bis die Antwort vollständig ist -
   * höchstens fünf Minuten. Läuft, solange `laeuft` gilt, darum bleibt die Eingabe gesperrt.
   */
  const nachladen = useCallback(
    async (id: number) => {
      const beginn = Date.now()
      for (;;) {
        try {
          const voll = await verlaufLaden(id)
          if (voll === null) return   // der Nutzer hat inzwischen ein anderes Gespräch gewählt
          if (voll.unterhaltung.laeuft) {
            await mitlaufen(id)
            if (gewuenscht.current === id) setHinweis('Die Verbindung war unterbrochen; der Assistent hat auf dem Server weitergearbeitet, der Verlauf ist vollständig.')
            return
          }
          if (voll.unterhaltung.laeuft === false || antwortVollstaendig(voll)) {
            setHinweis('Die Verbindung war kurz unterbrochen; der Verlauf wurde neu geladen.')
            return
          }
        } catch {
          // Noch keine Verbindung - gleich noch einmal, bis die Frist um ist.
        }
        if (Date.now() - beginn > NACHLADEN_HOECHSTENS_MS) {
          setFehler('Die Verbindung war unterbrochen, und nach fünf Minuten ist die Antwort noch nicht vollständig. Bitte das Gespräch in der Liste erneut öffnen.')
          return
        }
        await pause(NACHLADEN_ABSTAND_MS)
      }
    },
    [mitlaufen, verlaufLaden],
  )

  const streamen = useCallback(
    async (id: number, pfad: string, koerper?: unknown) => {
      const nummer = laufBeginnen()
      setFehler(null)
      setHinweis(null)
      setWartet(false)
      neueRunde.current = true
      // Jeder Lauf endet mit fertig, wartet oder fehler. Endet der Strom ohne (Proxy, Netzwechsel), ist er
      // unterwegs gerissen - der Server rechnet weiter, also nachsehen statt aufzuhören.
      let abgeschlossen = false
      try {
        // Ereignisse gehören nur auf den Schirm, solange dieses Gespräch das gewählte ist - sonst schrieb der
        // Auftakt eines neuen Gesprächs in den Verlauf des alten (Sandbox-Befund 17.09.2026, „roher ui-Block").
        await strom(pfad, koerper, (e) => {
          if (e.typ === 'fertig' || e.typ === 'wartet' || e.typ === 'fehler') abgeschlossen = true
          if (gewuenscht.current === id) anwenden(e)
        })
        if (!abgeschlossen) await nachladen(id)
      } catch (f) {
        if (istVerbindungsabbruch(f)) await nachladen(id)
        else if (gewuenscht.current === id) setFehler(fehlertext(f))
      } finally {
        laufBeenden(nummer)
        void listeLaden()
      }
    },
    [anwenden, laufBeenden, laufBeginnen, listeLaden, nachladen],
  )

  const oeffnen = useCallback(
    async (id: number) => {
      mitlaufenLoesen()
      setFehler(null)
      setHinweis(null)
      gewuenscht.current = id
      const voll = await verlaufLaden(id)
      if (voll === null) return
      setAktuell(id)
      // Nach dem Neuladen der Seite oder am nächsten Morgen: arbeitet der Lauf noch, mitlaufen.
      if (voll.unterhaltung.laeuft) void begleiten(id)
    },
    [begleiten, mitlaufenLoesen, verlaufLaden],
  )

  const neu = useCallback(
    async (frage?: string) => {
      mitlaufenLoesen()
      gewuenscht.current = null   // bis die neue Unterhaltung da ist, darf kein alter Verlauf mehr landen
      const u = await api.unterhaltungAnlegen(bereich)
      gewuenscht.current = u.id
      setAktuell(u.id)
      setEintraege([])
      setWartet(false)
      if (frage) {
        setEintraege([{ id: -1, rolle: 'user', text: frage, werkzeuge: [], angelegt_am: new Date().toISOString() }])
        await streamen(u.id, `/api/assistent/unterhaltungen/${u.id}/nachrichten`, { text: frage })
      } else {
        await streamen(u.id, `/api/assistent/unterhaltungen/${u.id}/auftakt`)
      }
    },
    [bereich, mitlaufenLoesen, streamen],
  )

  // Eine Nachricht, die eine andere Seite mitgibt (`navigate('/assistent', { state: { nachricht } })`,
  // z.B. „Mit dem Assistenten besprechen" auf dem Excel-Import), hat Vorrang vor `?frage=` aus der
  // Adresse. Beide starten ein neues Gespräch mit dem Text als erster Nutzernachricht.
  const nachrichtAusZustand = (location.state as { nachricht?: string } | null)?.nachricht
  const mitgegebeneFrage = nachrichtAusZustand ?? suchparameter.get('frage')
  // Ein bestimmtes Gespräch (`?unterhaltung=12`, von „Heute": ein liegen gebliebener Faden) - ohne Frage.
  const mitgegebenesGespraech = Number(suchparameter.get('unterhaltung')) || null

  /** Frage bzw. Nachricht aus Adresse und Verlaufseintrag nehmen, damit ein Neuladen sie nicht erneut schickt. */
  const mitgegebeneFrageLeeren = useCallback(() => {
    if (nachrichtAusZustand) navigate(basis, { replace: true, state: null })
    else setSuchparameter({}, { replace: true })
  }, [basis, nachrichtAusZustand, navigate, setSuchparameter])

  // Beim Öffnen: eine mitgegebene Frage (Disposition, Excel-Import), sonst das letzte Gespräch,
  // sonst ein neues mit Auftakt.
  // Eine neue Frage, während die Seite schon offen ist (Kopfzeilen-Feld auf /assistent):
  // ein neues Gespräch, nicht nur ein geleertes Feld.
  const letzteFrage = useRef<string | null>(null)
  useEffect(() => {
    const frage = mitgegebeneFrage
    if (!gestartet.current || !frage || frage === letzteFrage.current) return
    letzteFrage.current = frage
    mitgegebeneFrageLeeren()
    void neu(frage).catch((f) => setFehler(fehlertext(f)))
  }, [mitgegebeneFrage, mitgegebeneFrageLeeren, neu])

  /** Ein Gespräch aus einem Link öffnen; gibt es es nicht mehr, sagt das eine Meldung und es geht normal weiter. */
  const gespraechAusLink = useCallback(
    async (id: number): Promise<boolean> => {
      setSuchparameter({}, { replace: true })
      try {
        await oeffnen(id)
        return true
      } catch (f) {
        setFehler(`Das Gespräch ${id} ließ sich nicht öffnen: ${fehlertext(f)}`)
        return false
      }
    },
    [oeffnen, setSuchparameter],
  )

  // Ein Link auf ein Gespräch, während die Seite schon offen ist.
  useEffect(() => {
    if (!gestartet.current || mitgegebenesGespraech === null || mitgegebeneFrage) return
    void gespraechAusLink(mitgegebenesGespraech)
  }, [gespraechAusLink, mitgegebeneFrage, mitgegebenesGespraech])

  useEffect(() => {
    if (gestartet.current) return
    gestartet.current = true
    const frage = mitgegebeneFrage
    letzteFrage.current = frage
    void (async () => {
      try {
        if (frage) {
          mitgegebeneFrageLeeren()
          await neu(frage)
          return
        }
        // Das Gespräch aus dem Link zuerst - gespraechAusLink leert die Adresse sofort, damit der Effekt
        // darüber es nicht ein zweites Mal öffnet.
        if (mitgegebenesGespraech !== null && (await gespraechAusLink(mitgegebenesGespraech))) {
          await listeLaden()
          return
        }
        const werte = await listeLaden()
        if (werte.length > 0) await oeffnen(werte[0].id)
        else await neu()
      } catch (f) {
        setFehler(fehlertext(f))
      }
    })()
  }, [gespraechAusLink, listeLaden, mitgegebeneFrage, mitgegebeneFrageLeeren, mitgegebenesGespraech, neu, oeffnen])

  useEffect(() => {
    ende.current?.scrollIntoView({ block: 'end' })
  }, [eintraege, laeuft])

  /** Eine Nutzernachricht in Klartext abschicken - aus der Eingabezeile oder aus einem UI-Block. */
  const nachrichtSenden = useCallback(
    async (text: string) => {
      if (!text || aktuell === null || laeuft || wartet) return
      setEintraege((alt) => [...alt, { id: -Date.now(), rolle: 'user', text, werkzeuge: [], angelegt_am: new Date().toISOString() }])
      await streamen(aktuell, `/api/assistent/unterhaltungen/${aktuell}/nachrichten`, { text })
    },
    [aktuell, laeuft, wartet, streamen],
  )

  async function senden() {
    const text = eingabe.trim()
    if (!text) return
    setEingabe('')
    await nachrichtSenden(text)
  }

  const ausBlock = useCallback((text: string) => void nachrichtSenden(text), [nachrichtSenden])

  function entscheiden(id: string, erlaubt: boolean) {
    entscheidenViele([id], erlaubt)
  }

  /** Mehrere wartende Aktionen auf einmal entscheiden. „MVN für alle Artikel" ergab 65 Karten mit je
   * einem Knopf - sicher, aber unbedienbar (Befund 19.09.2026). Der Nutzer sieht die Zahl und die
   * Liste und entscheidet einmal. */
  function entscheidenViele(kennungen: string[], erlaubt: boolean) {
    if (aktuell === null || kennungen.length === 0) return
    const gesetzt = new Set(kennungen)
    setEintraege((alt) =>
      alt.map((e) => ({
        ...e,
        werkzeuge: e.werkzeuge.map((w) => (gesetzt.has(w.id) ? { ...w, zustand: erlaubt ? 'laeuft' : 'abgelehnt' } : w)),
      })),
    )
    const entscheidungen = Object.fromEntries(kennungen.map((k) => [k, erlaubt]))
    void streamen(aktuell, `/api/assistent/unterhaltungen/${aktuell}/freigabe`, { entscheidungen })
  }

  /**
   * Eine Datei anhängen: ein PDF wird als Import hochgeladen, eine Excel-Datei als Excel-Import-Lauf
   * (nur gelesen, nichts gebucht). Danach bekommt der Assistent eine Nutzernachricht, die ihm sagt,
   * wo die Datei liegt - alles Weitere läuft im Gespräch über seine Werkzeuge.
   */
  async function hochladen(datei: File) {
    if (aktuell === null) return
    setFehler(null)
    const istExcel = bereich === 'verpackung' && /\.xls[xm]$/i.test(datei.name)
    setLiestExcel(istExcel)
    try {
      let text: string
      if (bereich === 'notierungen') {
        // Eine Notierung wird synchron gelesen; der Assistent bekommt den Lauf und entscheidet mit dem Menschen.
        const lauf = await notierungenApi.hochladen([datei])
        text = `Ich habe die Notierung „${datei.name}“ hochgeladen. Sie liegt als Lauf ${lauf.id}${lauf.status === 'uebernommen' ? ' und wurde schon übernommen' : ''}. Bitte prüfen Sie die Positionen und sagen Sie mir, was auffällt.`
      } else if (istExcel) {
        const lauf = await api.excelWarten((await api.excelHochladen(datei)).id)
        text = `Ich habe die Excel-Datei „${datei.name}“ hochgeladen. Sie liegt als Excel-Import-Lauf ${lauf.id}; gebucht ist noch nichts. Bitte lesen Sie den Plan, besprechen Sie Warnungen und Vorschläge mit mir und führen Sie ihn erst nach meiner Freigabe aus.`
      } else {
        const lauf = await api.hochladen(datei)
        text = `Ich habe die Datei „${datei.name}" hochgeladen. Sie liegt als Import ${lauf.id}. Bitte prüfen Sie den Import und sagen Sie mir, ob er freigegeben werden kann.`
      }
      // Schon vor dem Senden freigeben: ab hier gilt „Der Assistent arbeitet", nicht mehr „wird gelesen".
      setLiestExcel(false)
      await nachrichtSenden(text)
    } catch (f) {
      setFehler(fehlertext(f))
    } finally {
      setLiestExcel(false)
    }
  }

  async function loeschen(id: number) {
    await api.unterhaltungLoeschen(id)
    const werte = await listeLaden()
    if (id === aktuell) {
      if (werte.length > 0) await oeffnen(werte[0].id)
      else await neu()
    }
  }

  const gesperrt = laeuft || liestExcel
  // Gespräch wechseln geht nicht, solange dieser Browser einen Lauf schickt - wohl aber, solange er nur mitläuft.
  const wechselGesperrt = (laeuft && !aufServer) || liestExcel

  return (
    <div className="chat">
      <aside className="chat-liste">
        <button className="haupt" disabled={wechselGesperrt} onClick={() => void neu()}>
          Neues Gespräch
        </button>
        <ul>
          {unterhaltungen.map((u) => (
            <li key={u.id} className={u.id === aktuell ? 'aktiv' : ''}>
              <button className="chat-eintrag" disabled={wechselGesperrt} onClick={() => void oeffnen(u.id)}>
                <span>{u.titel ?? `Gespräch ${u.id}`}</span>
                <span
                  className="hinweis"
                  title={
                    u.id === aktuell && tokens.eingabe > 0
                      ? `Token dieser Sitzung: ${stueck(tokens.eingabe)} Eingabe, ${stueck(tokens.ausgabe)} Ausgabe`
                      : undefined
                  }
                >
                  {zeitpunkt(u.geaendert_am)}
                  {u.wartet ? ' · wartet' : ''}
                  {u.laeuft ? ' · läuft' : ''}
                </span>
              </button>
              <button
                className="chat-loeschen"
                title="Gespräch löschen"
                disabled={wechselGesperrt || (laeuft && u.id === aktuell)}
                onClick={() => void loeschen(u.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <p className="hinweis">{vorgaben.kann}</p>
      </aside>

      <section className="chat-verlauf">
        <Meldungen fehler={fehler} />
        {hinweis && <Meldung art="hinweis">{hinweis}</Meldung>}
        <div className="chat-nachrichten">
          {eintraege.map((e, n) => (
            <div key={e.id} className={`chat-nachricht ${e.rolle}${istAbbruch(e) ? ' abbruch' : ''}`}>
              {istAbbruch(e) ? (
                <p className="chat-abbruch">
                  <span className="status verworfen">Abbruchvermerk</span> {e.text}
                </p>
              ) : e.rolle === 'user' ? (
                <p>{zeilenMitUmbruch(e.text.split('\n'), `u${e.id}`)}</p>
              ) : (
                <>
                  <Sammelfreigabe
                    wartend={e.werkzeuge.filter((w) => w.zustand === 'wartet')}
                    gesperrt={gesperrt}
                    entscheiden={entscheidenViele}
                  />
                  {e.werkzeuge.map((w) => (
                    <WerkzeugKarte key={w.id} werkzeug={w} entscheiden={entscheiden} gesperrt={gesperrt} />
                  ))}
                  {e.text && (
                    <div className="chat-text">
                      {markdown(e.text, {
                        senden: ausBlock,
                        gesperrt: gesperrt || wartet,
                        // Die Nutzernachrichten nach dieser Antwort: daran erkennt ein Formular oder eine
                        // Auswahl nach dem Neuladen, dass sie schon beantwortet ist.
                        spaetereAntworten: eintraege.slice(n + 1).filter((s) => s.rolle === 'user').map((s) => s.text),
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {laeuft && !aufServer && <p className="hinweis chat-arbeitet">Der Assistent arbeitet.</p>}
          {laeuft && aufServer && (
            <p className="hinweis chat-arbeitet chat-auf-server">
              Läuft noch auf dem Server - die Antwort erscheint hier, sobald sie kommt. Bis dahin ist Senden gesperrt
            </p>
          )}
          {liestExcel && <p className="chat-liest-excel chat-arbeitet">Die Excel-Datei wird gelesen, das kann einige Minuten dauern.</p>}
          <div ref={ende} />
        </div>

        <div className="chat-eingabe">
          <textarea
            value={eingabe}
            placeholder={
              wartet
                ? 'Erst die wartende Aktion ausführen oder ablehnen.'
                : aufServer
                  ? 'Der Assistent arbeitet noch auf dem Server - Senden geht, sobald er fertig ist.'
                  : vorgaben.platzhalter
            }
            disabled={gesperrt || wartet || aktuell === null}
            onChange={(ev) => setEingabe(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' && !ev.shiftKey) {
                ev.preventDefault()
                void senden()
              }
            }}
          />
          <div className="leiste" style={{ marginBottom: 0 }}>
            <button className="haupt" disabled={gesperrt || wartet || !eingabe.trim()} onClick={() => void senden()}>
              Senden
            </button>
            <label className="knopf">
              {ANHANG[bereich].beschriftung}
              <input
                type="file"
                accept={ANHANG[bereich].annehmen}
                style={{ display: 'none' }}
                disabled={gesperrt || wartet}
                onChange={(ev) => {
                  const datei = ev.target.files?.[0]
                  if (datei) void hochladen(datei)
                  ev.target.value = ''
                }}
              />
            </label>
            <span className="hinweis">Enter sendet, Umschalt+Enter macht eine neue Zeile.</span>
            <button className="chat-zur-seite" onClick={() => navigate(vorgaben.zurSeite.pfad)}>
              {vorgaben.zurSeite.titel}
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
